import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Paper,
} from '@mui/material';
import PortalSnackbar from './Common/PortalSnackbar';
import {
  Save as SaveIcon,
  Palette as PaletteIcon,
  Style as StyleIcon,
  LocalFlorist as FlowerIcon,
  Add as AddIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import api from '../utils/api';
import useThemeStyles from '../theme/useThemeStyles';

const WEDDING_TYPES = [
  { value: 'luxury', label: 'Luxury' },
  { value: 'traditional', label: 'Traditional' },
  { value: 'destination', label: 'Destination' },
  { value: 'micro-wedding', label: 'Micro Wedding' },
  { value: 'elopement', label: 'Elopement' },
  { value: 'diy', label: 'DIY Wedding' },
  { value: 'intimate', label: 'Intimate' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'garden', label: 'Garden' },
  { value: 'beach', label: 'Beach' },
  { value: 'barn', label: 'Barn' },
  { value: 'urban', label: 'Urban' },
  { value: 'other', label: 'Other' }
];

const VIBE_TAGS = [
  'romantic', 'minimal', 'garden', 'editorial', 'festival', 'rustic',
  'elegant', 'bohemian', 'vintage', 'modern', 'classic', 'glamorous',
  'whimsical', 'industrial', 'cozy', 'sophisticated', 'playful', 'timeless', 'eclectic', 'natural'
];

const FORMALITY_OPTIONS = [
  { value: 'very-formal', label: 'Very Formal' },
  { value: 'formal', label: 'Formal' },
  { value: 'semi-formal', label: 'Semi-Formal' },
  { value: 'casual', label: 'Casual' },
  { value: 'cocktail', label: 'Cocktail' }
];

const DIY_LEVELS = [
  { value: 'none', label: 'None - Hiring professionals' },
  { value: 'some', label: 'Some - Mix of DIY and professional' },
  { value: 'lots', label: 'Lots - Mostly DIY' }
];

const DECORATION_OPTIONS = [
  'fairy-lights', 'candles', 'draping', 'balloons', 'bunting', 'lanterns',
  'vintage-furniture', 'photo-booth', 'backdrop', 'centerpieces', 'table-runners',
  'chair-covers', 'arch', 'aisle-runner', 'signage', 'other'
];

const FLORAL_OPTIONS = [
  'roses', 'peonies', 'hydrangeas', 'eucalyptus', 'babys-breath', 'dahlias',
  'orchids', 'lilies', 'tulips', 'sunflowers', 'lavender', 'wildflowers',
  'succulents', 'greenery', 'other'
];

const PRESET_COLOR_PALETTES = [
  { name: 'Blush & Gold', colors: ['#FFB6C1', '#FFD700', '#FFF8DC'] },
  { name: 'Navy & Burgundy', colors: ['#000080', '#800020', '#F5F5DC'] },
  { name: 'Sage & Terracotta', colors: ['#87A96B', '#CC5500', '#FAF0E6'] },
  { name: 'Dusty Blue & Mauve', colors: ['#B0C4DE', '#E0B0FF', '#F5F5F5'] },
  { name: 'Forest Green & Gold', colors: ['#228B22', '#FFD700', '#FFF8DC'] },
  { name: 'Black & White', colors: ['#000000', '#FFFFFF', '#C0C0C0'] },
];

function WeddingStyleTheme({ embedded }) {
  const styles = useThemeStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    // New structured fields
    weddingType: '',
    vibeTags: [],
    formality: '',
    diyLevel: 'none',
    decorations: [],
    florals: [],
    customColorPalette: [],
    // Legacy fields (for backward compatibility)
    theme: '',
    season: '',
    colorPalette: '',
    customColors: [],
    decorationStyle: '',
    flowerPreferences: [],
    specialElements: '',
    inspirationNotes: '',
    styleNotes: '',
    dislikes: '',
    hobbies: '',
    supplierNotes: ''
  });
  const [newColorHex, setNewColorHex] = useState('#000000');
  const [newColorName, setNewColorName] = useState('');

  useEffect(() => {
    fetchStyleDetails();
  }, []);

  const fetchStyleDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/wedding-details/style');
      if (response.data) {
        setFormData({
          weddingType: response.data.weddingType || '',
          vibeTags: response.data.vibeTags || [],
          formality: response.data.formality || '',
          diyLevel: response.data.diyLevel || 'none',
          decorations: response.data.decorations || [],
          florals: response.data.florals || [],
          customColorPalette: response.data.customColorPalette || [],
          // Legacy fields
          theme: response.data.theme || '',
          season: response.data.season || '',
          colorPalette: response.data.colorPalette || '',
          customColors: response.data.customColors || [],
          decorationStyle: response.data.decorationStyle || '',
          flowerPreferences: response.data.flowerPreferences || [],
          specialElements: response.data.specialElements || '',
          inspirationNotes: response.data.inspirationNotes || '',
          styleNotes: response.data.styleNotes || '',
          dislikes: response.data.dislikes || '',
          hobbies: response.data.hobbies || '',
          supplierNotes: response.data.supplierNotes || ''
        });
      }
    } catch (error) {
      setError('Failed to load style details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      await api.put('/api/wedding-details/style', formData);
      setSuccess('Style & theme saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to save style details');
    } finally {
      setSaving(false);
    }
  };

  const toggleTag = (fieldName, tag) => {
    const currentTags = formData[fieldName] || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    setFormData({ ...formData, [fieldName]: newTags });
  };

  const addCustomColor = () => {
    if (newColorHex && newColorName.trim()) {
      setFormData({
        ...formData,
        customColorPalette: [
          ...formData.customColorPalette,
          { hex: newColorHex, name: newColorName.trim() }
        ]
      });
      setNewColorHex('#000000');
      setNewColorName('');
    }
  };

  const removeCustomColor = (index) => {
    setFormData({
      ...formData,
      customColorPalette: formData.customColorPalette.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={embedded ? { p: 0 } : styles.container.page}>
      <Paper
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          p: embedded ? 2 : 3,
          border: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, px: 2 }}>
          Style & Theme
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
          Define the aesthetic and atmosphere for your special day. This helps us match you with the perfect suppliers!
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Grid container spacing={embedded ? 2 : 3}>
          {/* Wedding Type & Formality */}
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'white', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={embedded ? { py: 1.5, px: 2, '&:last-child': { pb: 1.5 } } : styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <StyleIcon color="primary" />
                <Typography variant="h6" sx={styles.text.sectionTitle}>
                  Wedding Type & Formality
                </Typography>
              </Box>
              
              <Box sx={styles.patterns.formContainer}>
                <FormControl fullWidth>
                  <InputLabel>Wedding Type</InputLabel>
                  <Select
                    value={formData.weddingType}
                    onChange={(e) => setFormData({ ...formData, weddingType: e.target.value })}
                    label="Wedding Type"
                  >
                    <MenuItem value="">Select...</MenuItem>
                    {WEDDING_TYPES.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Formality Level</InputLabel>
                  <Select
                    value={formData.formality}
                    onChange={(e) => setFormData({ ...formData, formality: e.target.value })}
                    label="Formality Level"
                  >
                    <MenuItem value="">Select...</MenuItem>
                    {FORMALITY_OPTIONS.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>DIY Level</InputLabel>
                  <Select
                    value={formData.diyLevel}
                    onChange={(e) => setFormData({ ...formData, diyLevel: e.target.value })}
                    label="DIY Level"
                  >
                    {DIY_LEVELS.map(level => (
                      <MenuItem key={level.value} value={level.value}>
                        {level.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </CardContent>
          </Card>
        </Grid>

          {/* Vibe Tags */}
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'white', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={embedded ? { py: 1.5, px: 2, '&:last-child': { pb: 1.5 } } : styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <StyleIcon color="primary" />
                <Typography variant="h6" sx={styles.text.sectionTitle}>
                  Vibe & Style Tags
                </Typography>
                <Tooltip title="Select all that describe your wedding vibe">
                  <InfoIcon fontSize="small" color="action" />
                </Tooltip>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {VIBE_TAGS.map(tag => (
                  <Chip
                    key={tag}
                    label={tag.charAt(0).toUpperCase() + tag.slice(1)}
                    onClick={() => toggleTag('vibeTags', tag)}
                    color={formData.vibeTags.includes(tag) ? 'primary' : 'default'}
                    variant={formData.vibeTags.includes(tag) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

          {/* Color Palette */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'white', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={embedded ? { py: 1.5, px: 2, '&:last-child': { pb: 1.5 } } : styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <PaletteIcon color="primary" />
                <Typography variant="h6" sx={styles.text.sectionTitle}>
                  Color Palette
                </Typography>
              </Box>
              
              {/* Preset Palettes */}
              <Typography variant="subtitle2" gutterBottom>
                Preset Palettes
              </Typography>
              <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {PRESET_COLOR_PALETTES.map(palette => (
                  <Box
                    key={palette.name}
                    sx={{
                      p: 2,
                      border: '2px solid',
                      borderColor: formData.colorPalette === palette.name ? 'primary.main' : 'grey.300',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': { borderColor: 'primary.light' }
                    }}
                    onClick={() => setFormData({ ...formData, colorPalette: palette.name })}
                  >
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {palette.name}
                    </Typography>
                    <Box sx={styles.layout.flexRow} gap={1}>
                      {palette.colors.map((color, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: color,
                            border: '1px solid rgba(0,0,0,0.1)',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>

              {/* Custom Color Builder */}
              <Typography variant="subtitle2" gutterBottom>
                Build Your Custom Palette
              </Typography>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <TextField
                  type="color"
                  label="Colour"
                  value={newColorHex}
                  onChange={(e) => setNewColorHex(e.target.value)}
                  sx={{ width: 100 }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Colour Name"
                  value={newColorName}
                  onChange={(e) => setNewColorName(e.target.value)}
                  placeholder="e.g., Blush Pink"
                  fullWidth
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addCustomColor}
                  disabled={!newColorName.trim()}
                >
                  Add
                </Button>
              </Box>

              {/* Custom Colors Display */}
              {formData.customColorPalette.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                  {formData.customColorPalette.map((color, idx) => (
                    <Chip
                      key={idx}
                      label={color.name}
                      sx={{
                        bgcolor: color.hex,
                        color: '#fff',
                        '& .MuiChip-label': { fontWeight: 'bold' }
                      }}
                      onDelete={() => removeCustomColor(idx)}
                    />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

          {/* Decorations */}
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'white', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={embedded ? { py: 1.5, px: 2, '&:last-child': { pb: 1.5 } } : styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <StyleIcon color="primary" />
                <Typography variant="h6" sx={styles.text.sectionTitle}>
                  Decorations
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {DECORATION_OPTIONS.map(decoration => (
                  <Chip
                    key={decoration}
                    label={decoration.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    onClick={() => toggleTag('decorations', decoration)}
                    color={formData.decorations.includes(decoration) ? 'primary' : 'default'}
                    variant={formData.decorations.includes(decoration) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

          {/* Florals */}
          <Grid item xs={12} md={6}>
            <Card sx={{ bgcolor: 'white', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={embedded ? { py: 1.5, px: 2, '&:last-child': { pb: 1.5 } } : styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <FlowerIcon color="primary" />
                <Typography variant="h6" sx={styles.text.sectionTitle}>
                  Florals
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {FLORAL_OPTIONS.map(floral => (
                  <Chip
                    key={floral}
                    label={floral.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                    onClick={() => toggleTag('florals', floral)}
                    color={formData.florals.includes(floral) ? 'primary' : 'default'}
                    variant={formData.florals.includes(floral) ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

          {/* Personal Style & Preferences */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'white', boxShadow: 'none', border: '1px solid rgba(0, 0, 0, 0.08)' }}>
            <CardContent sx={embedded ? { py: 1.5, px: 2, '&:last-child': { pb: 1.5 } } : styles.spacing.card}>
              <Typography variant="h6" sx={styles.text.sectionTitle} mb={2}>
                Personal Style & Preferences
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Style Notes"
                    value={formData.styleNotes}
                    onChange={(e) => setFormData({ ...formData, styleNotes: e.target.value })}
                    placeholder="e.g., 'Love blush pink and gold', 'Prefer minimalist style'"
                    helperText="Colours, aesthetics, or vibes you love"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Dislikes or Sensitivities"
                    value={formData.dislikes}
                    onChange={(e) => setFormData({ ...formData, dislikes: e.target.value })}
                    placeholder="Anything you'd prefer to avoid?"
                    helperText="e.g., hates pink flowers, avoids rustic style"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Hobbies or Interests"
                    value={formData.hobbies}
                    onChange={(e) => setFormData({ ...formData, hobbies: e.target.value })}
                    placeholder="e.g., 'Love hiking', 'Into vintage cars', 'Photography enthusiast'"
                    helperText="Helps photographers/planners personalise details"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Notes for Suppliers"
                    value={formData.supplierNotes}
                    onChange={(e) => setFormData({ ...formData, supplierNotes: e.target.value })}
                    placeholder="Special requests, personality notes, or preferences"
                    helperText="Any other information suppliers should know"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        </Grid>

        <Box sx={{ ...styles.layout.flexCenter, mt: 3 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
            disabled={saving}
            size="large"
          >
            {saving ? 'Saving...' : 'Save Style & Theme'}
          </Button>
        </Box>
      </Paper>

      {/* Toast Notifications */}
      <PortalSnackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>
          {error}
        </Alert>
      </PortalSnackbar>

      <PortalSnackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ width: '100%' }}>
          {success}
        </Alert>
      </PortalSnackbar>
    </Box>
  );
}

export default WeddingStyleTheme;
